// Time display helpers. Two rules, learned from user testing:
//  1. Plain-language labels — users don't read "GMT+8" / "GMT-3". Local time is
//     tagged "local"; the canonical/verification time is tagged "UTC".
//  2. UTC and local use the SAME "MM/DD, HH:mm:ss" format so a UTC/local pair
//     lines up visually (important on the provably-fair screens where the user
//     cross-checks against Etherscan, which shows UTC).

/**
 * A gaming-round date (YYYY-MM-DD) as Brazilians read it: DD/MM/YYYY.
 *
 * The ISO form is a data key — it identifies the round's day and is compared
 * and queried with — so it stays as it is everywhere except the screen.
 */
export function brDayLabel(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

// Compact local time for list rows (My Bets, Draw Archive cards). "12/07, 01:10".
// No " local" suffix: it was English in every locale, and these rows are always
// the viewer's own time — the archive detail sheet is where UTC and local are
// labelled side by side.
export function localTimeWithTz(seconds: number): string {
  if (!seconds) return '--';
  try {
    const time = new Intl.DateTimeFormat([], {
      month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(new Date(seconds * 1000));
    return time;
  } catch {
    return '--';
  }
}

// Full UTC date-time (matches Etherscan). Pairs with localDateTime below.
export function utcDateTime(seconds: number): string {
  if (!seconds) return '--';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false, timeZone: 'UTC',
    }).format(new Date(seconds * 1000));
  } catch {
    return '--';
  }
}

// Full local date-time, same format as utcDateTime.
export function localDateTime(seconds: number): string {
  if (!seconds) return '--';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    }).format(new Date(seconds * 1000));
  } catch {
    return '--';
  }
}

// 短日期 + 时间，跟着查看者的语言走，不写死 locale、不写死时区。
//
// 和上面的 localDateTime 是两码事，别当重复删掉：localDateTime 是给可验证开奖
// 的 UTC/本地对照两栏用的，两栏要用同一套字符规则才能对得整齐，所以它故意钉死
// en-GB 的 "MM/DD, HH:mm:ss"（连秒带 en-GB 都是为了那个对齐,不能改)。这个函数
// 服务的是普通玩家看的账单/详情小标题——阿根廷玩家应该看到阿根廷人认得出的日期
// 顺序，不是被钉死的英式顺序；账单标题上秒也是噪音，所以不带秒。参数用调用方
// 传进来的 language（i18n store 里存的选择），不用 `new Intl.DateTimeFormat([])`
// 隐式取设备 locale —— 玩家在应用里选的语言可能和手机系统语言不一样，历史表
// (MilharStatsPage 的 HistoryTable) 就是这么做的，这里跟它保持一致，同一天不能
// 在两个界面显示成两种日期形状。
export function shortDateTime(seconds: number, language: string): string {
  if (!seconds) return '';
  try {
    const date = new Date(seconds * 1000);
    const day = date.toLocaleDateString(language, { day: '2-digit', month: '2-digit' });
    const time = date.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit', hour12: false });
    return `${day} ${time}`;
  } catch {
    return '';
  }
}

// Clock only — "18:39". For lists that carry the date in a day heading above,
// so every row does not repeat it. (The old rows printed a full "09/08, 18:39"
// next to an ISO "2026-08-09" in the same line: two date formats, one row.)
export function localClock(seconds: number): string {
  if (!seconds) return '--:--';
  try {
    return new Intl.DateTimeFormat([], { hour: '2-digit', minute: '2-digit', hour12: false })
      .format(new Date(seconds * 1000));
  } catch {
    return '--:--';
  }
}

// "14:00:07" — the raffle entry list needs seconds: serials are handed out in
// entry order and a bot burst puts dozens of entries inside the same minute,
// so a minute-only clock reads as "everyone at 14:00" and explains nothing.
export function localClockSec(seconds: number): string {
  if (!seconds) return '--:--:--';
  try {
    return new Intl.DateTimeFormat([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })
      .format(new Date(seconds * 1000));
  } catch {
    return '--:--:--';
  }
}

// `localListTime` with seconds: "14:00:07" on the reference day, "13/09
// 08:41:22" on any other day. `refSeconds` is the instant the row is read
// against (the draw time for an entry list), not necessarily now.
export function localListTimeSec(seconds: number, refSeconds: number): string {
  if (!seconds) return '--';
  const clock = localClockSec(seconds);
  if (clock === '--:--:--') return '--';
  const sameDay = localDayKey(seconds) === localDayKey(refSeconds);
  return sameDay ? clock : `${localDayShort(seconds)} ${clock}`;
}

// Stable key for "which local calendar day is this", e.g. "2026-08-09".
export function localDayKey(seconds: number): string {
  if (!seconds) return '';
  const d = new Date(seconds * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Day heading for anything older than yesterday — "09/08".
export function localDayShort(seconds: number): string {
  if (!seconds) return '--';
  try {
    return new Intl.DateTimeFormat([], { day: '2-digit', month: '2-digit' })
      .format(new Date(seconds * 1000));
  } catch {
    return '--';
  }
}

// Day heading for a row from a past year — "09/08/2025". Same idea as
// localDayShort, but carries the year so a group from last year is never
// mislabeled as this year's.
export function localDayShortWithYear(seconds: number): string {
  if (!seconds) return '--';
  try {
    return new Intl.DateTimeFormat([], { day: '2-digit', month: '2-digit', year: 'numeric' })
      .format(new Date(seconds * 1000));
  } catch {
    return '--';
  }
}

// "MM/DD, HH:mm" via toLocaleString — the Notices list and the Feedback tab
// (FeedbackPanel's row list, FeedbackDetail's submission/reply timestamps)
// all render through this one function so their local-time format never
// drifts apart. Deliberately not one of the Intl.DateTimeFormat helpers
// above: this uses the browser's own locale ordering (toLocaleString with no
// explicit locale), not the fixed en-GB/[]-with-hour12:false shape those use.
export function formatLocalTime(seconds: number): string {
  if (!seconds) return '--';
  try {
    return new Date(seconds * 1000).toLocaleString([], { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '--';
  }
}

// List-row timestamp that drops the date on today's rows — "15:59" today,
// "16/08 15:59" otherwise.
//
// The transaction list used to print a full "2026/08/16 15:59:52" on every
// row. Year and seconds are noise there: the year is almost always the
// current one, and no one reconciles a deposit to the second. The precise
// timestamp still lives in the detail sheet, which is one tap away.
//
// `nowSeconds` is passed in rather than read from the clock so the caller
// decides the comparison instant (and so this stays a pure function).
export function localListTime(seconds: number, nowSeconds: number): string {
  if (!seconds) return '--';
  const clock = localClock(seconds);
  if (clock === '--:--') return '--';
  const sameDay = localDayKey(seconds) === localDayKey(nowSeconds);
  return sameDay ? clock : `${localDayShort(seconds)} ${clock}`;
}
